import { createLogger } from "@/logger";

const log = createLogger("telemetry");

export type IdleTransition = "idle-enter" | "idle-exit" | null;

/** The window events that count as the user doing something. */
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"] as const;

export class InactivityMonitorService {
  private lastActivityAt = Date.now();
  private isListening = false;
  private wasIdle = false;
  private readonly idleThresholdMs: number;

  private readonly onActivity = () => {
    this.lastActivityAt = Date.now();
  };

  /**
   * @param idleThresholdMs - How long without input before {@link isIdle} reads true.
   */
  public constructor(idleThresholdMs = 30_000) {
    this.idleThresholdMs = idleThresholdMs;
  }

  public start(): void {
    if (this.isListening) return;
    this.isListening = true;
    log.info(`[${this.constructor.name}] Listening for activity events`);

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.onActivity, { passive: true });
    }
  }

  /** Removes the listeners {@link start} added; the idle clock stops advancing on input until started again. */
  public stop(): void {
    if (!this.isListening) return;
    this.isListening = false;

    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, this.onActivity);
    }
  }

  public get inactivityDurationMs(): number {
    return Date.now() - this.lastActivityAt;
  }

  public get isIdle(): boolean {
    return this.inactivityDurationMs >= this.idleThresholdMs;
  }

  public consumeIdleTransition(): IdleTransition {
    const currentlyIdle = this.isIdle;
    const transition: IdleTransition =
      currentlyIdle && !this.wasIdle ? "idle-enter" : !currentlyIdle && this.wasIdle ? "idle-exit" : null;
    this.wasIdle = currentlyIdle;
    return transition;
  }
}
